import {
  AssignMemberRoleForMeetingAdminRequest,
  AssignRoleRequest,
  BatchAssignRolesRequest,
  BatchCancelRolesRequest,
  CancelAttendanceRequest,
  CancelMemberRoleForMeetingAdminRequest,
  ClubAdminInviteRequest,
  CreateAddressRequest,
  CreateClubMemberRequest,
  CreateGuestUserRequest,
  CreateRecurringMeetingRequest,
  PatchUserRequest,
  UpdateAddressRequest,
  UpdateClubMemberRequest,
  UpdateRecurringMeetingRequest,
} from '../generated'

export interface FirebaseAppUser {
  idpId: string
  accessToken: string
}

export interface MeetingDetailsRequest {
  clubId: string
  meetingTimestampUtc: string
}

export interface AssignRoleRequestWithUrlParameters {
  clubId: string
  meetingTimestampUtc: string
  body: AssignRoleRequest
}

export interface CancelAttendanceRequestWithUrlParameters {
  clubId: string
  meetingTimestampUtc: string
  body: CancelAttendanceRequest
}

export interface PatchUserAppRequest {
  body: PatchUserRequest
}

export interface CreateGuestUserAppRequest {
  accessToken: string
  body: CreateGuestUserRequest
}

export interface AcceptInviteAppRequest {
  accessToken: string
  inviteCode: string
}

export interface FirebaseUserCredentials {
  name: string
  email: string
  firebaseUid: string
  accessToken: string
}

export interface AssignRoleResponse {
  title: string
  description: string
}

// The role id the backend assigns to the Speaker role. Selecting it prompts for
// speech details before booking; every other role books immediately.
export const SPEAKER_ROLE_ID = 7

export const ADMIN_ACCESS_ROLE_ID = 1

export const GUEST_ROLE_ID = 11

// Pathway enum code (e.g. 'DYNAMIC_LEADERSHIP'), as captured on the assign-role
// request. Labels/groups for display come from the GET /pathways metadata.
export type PathwayCode = NonNullable<AssignRoleRequest['pathway']>

export type SpeechDetails = Pick<
  AssignRoleRequest,
  'speechTitle' | 'speechDescription' | 'pathway' | 'pathwayLevel'
>

export type AssignRoleState = 'NOT_STARTED' | 'PROCESSING' | 'SUCCESS' | 'ERROR'

// Admin
export interface CreateClubMemberAppRequest {
  clubId: string
  body: CreateClubMemberRequest
}

export interface UpdateClubMemberAppRequest {
  clubId: string
  userId: string
  body: UpdateClubMemberRequest
}

export interface DeleteClubMemberAppRequest {
  clubId: string
  userId: string
}

export interface ClubAdminInviteAppRequest {
  clubId: string
  body: ClubAdminInviteRequest
}

export interface AssignMemberRoleForMeetingAdminAppRequest {
  clubId: string
  meetingTimestampUtc: string
  body: AssignMemberRoleForMeetingAdminRequest
}

export interface CancelMemberRoleForMeetingAdminAppRequest {
  clubId: string
  meetingTimestampUtc: string
  body: CancelMemberRoleForMeetingAdminRequest
}

export interface BatchAssignRolesAppRequest {
  clubId: string
  meetingTimestampUtc: string
  body: BatchAssignRolesRequest
}

export interface BatchCancelRolesAppRequest {
  clubId: string
  meetingTimestampUtc: string
  body: BatchCancelRolesRequest
}

// Club Admin — Addresses
// POST/PUT are multipart/form-data: the address payload travels as an
// application/json `address` part and the optional cover image as a `file`
// part. The image can only be set by uploading a file — there is no logoUrl
// field on the request body. Omitting `file` on update leaves the existing
// image untouched; set the request's `removeImage` flag to clear it instead.
export interface CreateAddressAppRequest {
  clubId: string
  body: CreateAddressRequest
  file?: File
}

export interface UpdateAddressAppRequest {
  clubId: string
  addressId: string
  body: UpdateAddressRequest
  file?: File
}

export interface DeleteAddressAppRequest {
  clubId: string
  addressId: string
}

// Club Admin — Schedule (recurring meetings)
export interface CreateScheduleAppRequest {
  clubId: string
  body: CreateRecurringMeetingRequest
}

export interface UpdateScheduleAppRequest {
  clubId: string
  meetingId: string
  body: UpdateRecurringMeetingRequest
}

export interface DeleteScheduleAppRequest {
  clubId: string
  meetingId: string
}

// Bulk member import
export type ImportRowStatus =
  | 'IMPORTABLE'
  | 'MISSING_NAME'
  | 'MISSING_EMAIL'
  | 'INVALID_EMAIL'
  | 'DUPLICATE_IN_FILE'
  | 'ALREADY_MEMBER'

export interface ImportPreviewRow {
  rowNumber: number
  name: string | null
  email: string | null
  status: ImportRowStatus
  message: string | null
}

export interface ImportPreviewSummary {
  totalRows: number
  importable: number
  invalid: number
  duplicatesInFile: number
  alreadyMembers: number
}

export interface ImportPreviewResponse {
  summary: ImportPreviewSummary
  rows: ImportPreviewRow[]
}
